import {ReactElement, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {Tabs} from "@zendeskgarden/react-tabs";
import {ThemeProvider} from "styled-components";
import {OriginalRevenueOperationalMetricValidKey, VersionType} from "../../../../__generated__/generated_types";
import {BudgetingType} from "../../../../BudgetingType";
import {AuthContext} from "../../../../contexts/AuthContext";
import {getBudgetYearForProperty, useProperties} from "../../../../contexts/properties/PropertiesContext";
import {useSettings} from "../../../../contexts/settings/SettingsContext";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";
import * as workflowCSS from "../../../../styles/workflows/workflowCSS.module.scss";
import * as workflowHeaderCSS from "../../../../components/workflows/workflow-header/styles/css.module.scss";
import WorkflowHeader from "../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowNavToggle from "../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowPageLabel from "../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../../components/workflows/workflow-step-nav/WorkflowStepNav";
import WorkflowYearTabs, {WORKFLOW_YEAR_TAB_BUDGET} from "../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import VZSaveStatus from "../../../../components/vz-ui/vz-save-status/VZSaveStatus";
import VZHotTableControls from "../../../../components/vz-ui/vz-hot-table/VZHotTableControls";
import "../../../../components/vz-ui/vz-hot-table/styles/vz-hot-table.scss";
import {ContextMenuItem, SimpleHoTChange, SimpleHoTWrapper} from "../../../admin/line-item-management/SimpleHoTWrapper";
import {getMonths} from "../../../../utils/months-helper";
import {IWorkflowPageProps, shouldUpdateWorkflowTab} from "../../logic/workflows";
import {useUTSVOperationalMetrics} from "../hooks/useUTSVOperationalMetrics";
import {utsvOpMetricQueryReturn} from "../utils/operational-metric-helpers";
import * as overrideCSS from "../average-rent/average-rent-growth/AverageRentGrowth.module.scss";
import Handsontable from "handsontable";

const MONTH_COUNT = 12;
const AVERAGE_COLUMN = MONTH_COUNT + 1;

type UserSpecifiedUnitCountRow = {
    isCompound: false;
    values: {label: string; values: (number | null)[]; average: number | null};
    extraData?: Record<string, unknown>;
};

function average(values: (number | null)[]): number | null {
    const numericValues = values.filter((value): value is number => value !== null);
    return numericValues.length === 0 ? null : +(numericValues.average()).toFixed(2);
}

function buildUserSpecifiedUnitCountRows(data: utsvOpMetricQueryReturn, versionType: VersionType, reforecastStartMonthIndex: number): UserSpecifiedUnitCountRow[] {
    const version = data.find(item => item.version === versionType);
    if (!version?.unitTypes) return [];
    const reforecastVersion = data.find(item => item.version === VersionType.Reforecast);
    const reforecastValuesByUnitType = new Map<string, (number | null)[]>();

    if (reforecastVersion?.unitTypes) {
        for (const unitType of reforecastVersion.unitTypes) {
            const values: (number | null)[] = [];
            for (let monthIndex = 0; monthIndex < MONTH_COUNT; monthIndex++) {
                const rawValue = monthIndex < reforecastStartMonthIndex
                    ? unitType.unitTypeCountMonthly[monthIndex]
                    : unitType.userSpecifiedUnitCount[monthIndex];
                const serverValue = rawValue ?? null;
                values.push(serverValue === null && monthIndex > 0 ? values[monthIndex - 1] ?? null : serverValue);
            }
            reforecastValuesByUnitType.set(unitType.unitTypeName, values);
        }
    }

    return version.unitTypes.slice().sort((left, right) => left.unitTypeName.localeCompare(right.unitTypeName)).map(unitType => {
            const displayOnlyColumns: number[] = [];
            const actualColumns: number[] = [];
            const values: (number | null)[] = [];
        const reforecastValues = reforecastValuesByUnitType.get(unitType.unitTypeName);
        const initialValue = versionType === VersionType.Budget ? reforecastValues?.[MONTH_COUNT - 1] ?? null : null;

        for (let monthIndex = 0; monthIndex < MONTH_COUNT; monthIndex++) {
            const rawValue = versionType === VersionType.Reforecast && monthIndex < reforecastStartMonthIndex
                ? unitType.unitTypeCountMonthly[monthIndex]
                : unitType.userSpecifiedUnitCount[monthIndex];
            const serverValue = rawValue ?? null;
            const previousValue = monthIndex === 0 ? initialValue : values[monthIndex - 1] ?? null;

            if (serverValue === null && previousValue !== null) {
                values.push(previousValue);
                displayOnlyColumns.push(monthIndex);
            } else {
                values.push(serverValue);
                if (serverValue !== null && !(versionType === VersionType.Reforecast && monthIndex < reforecastStartMonthIndex)) {
                    actualColumns.push(monthIndex);
                }
            }
        }

        return {
            isCompound: false,
            values: {label: unitType.unitTypeName, values, average: average(values)},
            extraData: {displayOnlyColumns, actualColumns, initialValue},
        };
    });
}

export default function UserSpecifiedUnitCount(props: IWorkflowPageProps): ReactElement {
    const {type} = useParams() as {type: BudgetingType};
    const auth = useContext(AuthContext);
    const {currentProperty} = useProperties();
    const {year} = useSettings();
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);
    const versionType = type === BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;
    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);
    const hotRef = useRef<Handsontable | null>(null);
    const [filterText, setFilterText] = useState("");
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    const {fetchMetricData, metricData, metricDataLoading, forecastLocks, lastSaved, savingValues, upsertValues} = useUTSVOperationalMetrics({
        property: currentProperty,
        year: type === BudgetingType.BUDGET ? year + 1 : year,
        versionType,
    });

    useEffect(() => {
        const updatedTab = shouldUpdateWorkflowTab(type, selectedTab);
        if (updatedTab !== false) setSelectedTab(updatedTab as string);
        fetchMetricData();
    }, [type]);

    const tableData = useMemo<UserSpecifiedUnitCountRow[]>(() => {
        if (!metricData || !currentProperty) return [];
        return buildUserSpecifiedUnitCountRows(metricData, versionType, currentProperty.reforecastStartMonthIndex);
    }, [metricData, currentProperty, versionType]);

    const filteredTableData = useMemo(() => {
        const normalizedFilter = filterText.trim().toLowerCase();
        if (normalizedFilter.length === 0) return tableData;
        return tableData.filter(row => row.values.label.toLowerCase().includes(normalizedFilter));
    }, [tableData, filterText]);

    const tableLocked = versionType === VersionType.Budget
        ? forecastLocks?.forecastLocks?.property.budgetLocked
        : forecastLocks?.forecastLocks?.property.reforecastLocked;

    function handleChange(changes: SimpleHoTChange[]): void {
        function monthIndexForColumn(col: number | string): number | undefined {
            if (typeof col === "number") {
                return col >= 1 && col <= MONTH_COUNT ? col - 1 : undefined;
            }

            const match = /^values\.(\d+)$/.exec(col);
            if (!match) return undefined;

            const monthIndex = Number(match[1]);
            return monthIndex >= 0 && monthIndex < MONTH_COUNT ? monthIndex : undefined;
        }

        const changedRows = new Set<UserSpecifiedUnitCountRow>();
        for (const change of changes) {
            if (!change.row.isCompound) {
                changedRows.add(change.row as UserSpecifiedUnitCountRow);

                const monthIndex = monthIndexForColumn(change.col);
                if (monthIndex !== undefined && (typeof change.newValue === "number" || change.newValue === null)) {
                    const row = change.row as UserSpecifiedUnitCountRow;
                    const displayOnlyColumns = row.extraData?.["displayOnlyColumns"] as number[] | undefined;
                    const displayOnly = new Set(displayOnlyColumns ?? []);
                    const actualColumns = new Set(row.extraData?.["actualColumns"] as number[] | undefined ?? []);

                    if (change.newValue === null) {
                        // Persist the deletion while continuing to display the prior
                        // value whenever one exists.
                        const initialValue = row.extraData?.["initialValue"] as number | null | undefined ?? null;
                        const previousValue = monthIndex === 0 ? initialValue : row.values.values[monthIndex - 1] ?? null;
                        if (previousValue !== null) {
                            row.values.values[monthIndex] = previousValue;
                            displayOnly.add(monthIndex);
                        } else {
                            displayOnly.delete(monthIndex);
                        }
                        actualColumns.delete(monthIndex);
                    } else {
                        // A direct edit turns a previously derived cell into a real value.
                        displayOnly.delete(monthIndex);
                        actualColumns.add(monthIndex);
                    }

                    // Keep all later null-derived cells aligned with the new default,
                    // stopping at the next value that came from the server.
                    const displayedValue = row.values.values[monthIndex] ?? null;
                    for (let followingMonth = monthIndex + 1; followingMonth < MONTH_COUNT && displayOnly.has(followingMonth); followingMonth++) {
                        row.values.values[followingMonth] = displayedValue;
                    }

                    if (row.extraData) {
                        row.extraData["displayOnlyColumns"] = Array.from(displayOnly);
                        row.extraData["actualColumns"] = Array.from(actualColumns);
                    }
                }
            }
        }
        for (const row of Array.from(changedRows)) {
            row.values.average = average(row.values.values);
        }

        const numericChanges = changes
            .map(change => ({change, monthIndex: monthIndexForColumn(change.col)}))
            .filter(({change, monthIndex}) => monthIndex !== undefined && (typeof change.newValue === "number" || change.newValue === null))
            .filter(change => {
                const displayOnlyColumns = change.change.row.extraData?.["displayOnlyColumns"] as number[] | undefined;
                return !(displayOnlyColumns?.includes(change.monthIndex as number) && change.change.newValue === change.change.previousValue);
            })
            .map(({change, monthIndex}) => [change.rowId, monthIndex as number, change.newValue as number | null] as [number, number, number | null]);

        if (numericChanges.length > 0) {
            upsertValues(tableData.map(row => ({label: row.values.label})), numericChanges, budgetYear, OriginalRevenueOperationalMetricValidKey.UserSpecifiedUnitCount);
        }
    }

    const contextMenu = useMemo<Record<string, ContextMenuItem>>(() => ({
        removeOverride: {
            name: "Remove Override",
            hidden: () => false,
            disabled: (_selectedRows, selectedCols) => !Array.from(selectedCols).some(col => col >= 1 && col <= MONTH_COUNT),
            callback: (selectedRows, selectedCols) => {
                const hot = hotRef.current;
                if (!hot) return;

                const monthColumns = Array.from(selectedCols).filter(col => col >= 1 && col <= MONTH_COUNT);
                const changes: [number, number, number | null][] = [];
                for (const rowIndex of Array.from(selectedRows)) {
                    const row = filteredTableData[rowIndex];
                    if (!row) continue;

                    for (const col of monthColumns) {
                        changes.push([rowIndex, col, null]);
                    }
                }

                if (changes.length > 0) {
                    hot.setDataAtCell(changes, "edit");
                }
            },
        },
    }), [filteredTableData]);

    return <ThemeProvider theme={ViziblyTheme}>
        <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
            <WorkflowHeader>
                <WorkflowHeader.LeftCol>
                    <div className={workflowHeaderCSS.rowItemsContainer}>
                        <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow}/>
                        <WorkflowPageLabel label="Unit Count Adjustment"/>
                    </div>
                </WorkflowHeader.LeftCol>
                <WorkflowHeader.RightCol>
                    <VZSaveStatus isSaving={savingValues} lastSaved={lastSaved}/>
                    <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={props.inUserWorkflow}/>
                </WorkflowHeader.RightCol>
            </WorkflowHeader>
            <div className={workflowCSS.bodyContainer}>
                {currentProperty && <>
                    <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.tabsContainer}>
                        <WorkflowYearTabs property={currentProperty} selectedTab={selectedTab} rawForecastLocks={forecastLocks}/>
                    </Tabs>
                    <div className={workflowCSS.tableWrapper}>
                        <VZHotTableControls
                            canUndo={canUndo}
                            canRedo={canRedo}
                            isDataLoading={metricDataLoading}
                            isSearchable
                            isUndoRedoable
                            canSuppressZeroes={false}
                            onUndo={() => hotRef.current?.undo()}
                            onRedo={() => hotRef.current?.redo()}
                            onFilterTextChange={setFilterText}
                            onSuppressZeroes={() => undefined}
                        />
                        <SimpleHoTWrapper
                            rows={filteredTableData}
                            withControls
                            columns={[
                                {data: "label"},
                                ...Array.from({length: MONTH_COUNT}, (_, index) => ({data: `values.${index}`, type: "numeric"})),
                                {data: "average", type: "numeric", readOnly: true},
                            ]}
                            dataLoading={metricDataLoading}
                            hotProps={{
                                renderAllRows: true,
                                colHeaders: ["Unit Type", ...getMonths(currentProperty), "Average"],
                                colWidths: [150, ...new Array(MONTH_COUNT).fill(80), 90],
                                width: "100%",
                                height: "60vh",
                                stretchH: "all",
                                fixedColumnsStart: 1,
                                autoRowSize: false,
                                autoColumnSize: false,
                                rowHeights: 60,
                                undo: true,
                                afterInit: function(this: Handsontable) {
                                    hotRef.current = this;
                                    setCanUndo(this.isUndoAvailable());
                                    setCanRedo(this.isRedoAvailable());
                                },
                                afterUndo: () => {
                                    setCanUndo(hotRef.current?.isUndoAvailable() ?? false);
                                    setCanRedo(hotRef.current?.isRedoAvailable() ?? false);
                                },
                                afterRedo: () => {
                                    setCanUndo(hotRef.current?.isUndoAvailable() ?? false);
                                    setCanRedo(hotRef.current?.isRedoAvailable() ?? false);
                                },
                                afterUndoStackChange: (_before, after) => {
                                    setCanUndo(after.length > 0);
                                    setCanRedo(false);
                                },
                            }}
                            contextMenu={contextMenu}
                            cellStyler={(_row, col, rowData) => {
                                const isReforecastLockedMonth = versionType === VersionType.Reforecast && col >= 1 && col <= currentProperty.reforecastStartMonthIndex;
                                const actualColumns = rowData.extraData?.["actualColumns"] as number[] | undefined;
                                return {
                                    isReadOnly: col === 0 || col === AVERAGE_COLUMN || tableLocked === true || isReforecastLockedMonth,
                                    cssClasses: col >= 1 && col <= MONTH_COUNT && actualColumns?.includes(col - 1) ? [overrideCSS.overridenValue] : undefined,
                                };
                            }}
                            onChange={handleChange}
                        />
                    </div>
                </>}
            </div>
        </div>
    </ThemeProvider>;
}
